import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  makeStyles,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  BugReport as BugReportIcon,
  Build as BuildIcon,
  Info as InfoIcon,
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  versionBadge: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  dateGroup: {
    marginBottom: theme.spacing(3),
  },
  dateHeader: {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  },
  updateItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  updateType: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  updateDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  dateInfo: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  contentContainer: {
    maxHeight: '60vh',
    overflowY: 'auto',
  },
}));

// ✅ CHANGELOG HARDCODED - Atualizações reais do sistema
const CHANGELOG = [
  // === 27 de Outubro de 2026 (v2.6) ===
  {
    type: "feature",
    title: "Sincronização Estável do WhatsApp Web e Blindagem Anti-Perda de Mensagens",
    description: "Integração nativa com a versão de produção oficial homologada pelo Baileys (2.3000.1043857760). Bloqueio rigoroso de versões instáveis (-alpha e -beta) do WPPConnect, eliminando a incompatibilidade de Protobuf e a perda silenciosa de mensagens recebidas. Fallback moderno para prevenção de sockets zumbis e cache em memória com 0ms de latência no handshake de conexões.",
    timestamp: new Date(2026, 9, 27, 10, 0),
  },
  {
    type: "bugfix",
    title: "Correção Definitiva de Encaminhamento de Localização no WhatsApp",
    description: "Implementação de suporte completo ao nó nativo locationMessage do Baileys com extração automática de latitude e longitude a partir de dataJson, links do Google Maps e body pipe format. Envio nativo estruturado com identificador isForwarded: true e inclusão na lista de mensagens permitidas, garantindo chegada ao destinatário e exibição perfeita no CRM.",
    timestamp: new Date(2026, 9, 27, 9, 45),
  },
  {
    type: "bugfix",
    title: "Eliminação do Loop Infinito no Botão e Comando 'Sair' do Bot",
    description: "Normalização regex tolerante para variações de texto digitadas ou clicadas ('sair', '[ Sair ]', '*[ Sair ]*', 'encerrar', 'fim'). Limpeza profunda de estado do atendimento no encerramento (queueId, userId, isBot e contadores zerados) e trava de segurança no FindOrCreateTicketService para impedir que mensagens subsequentes reengatem em loop na fila anterior.",
    timestamp: new Date(2026, 9, 27, 9, 30),
  },
  {
    type: "improvement",
    title: "Estabilidade de Sockets Baileys e Roteamento Seguro de Mensagens 1-a-1",
    description: "Substituição do comando de logout em sessões duplicadas por fechamento limpo de WebSocket, preservando as credenciais de autenticação do QR Code sem desconectar a sessão no aparelho. Limpeza prévia de instâncias em memória via removeWbot e roteamento direto de mensagens individuais para @s.whatsapp.net, evitando mensagens travadas em 1 tique cinza por chaves @lid sem pareamento.",
    timestamp: new Date(2026, 9, 27, 9, 15),
  },
  {
    type: "improvement",
    title: "Infraestrutura Docker e Suíte Dupla de 20 Testes Automatizados",
    description: "Orquestração completa dos contêineres PostgreSQL 13 (5432) e Redis 6 (6379) via Docker Compose com liberação proativa de portas ocupadas. Duas baterias completas de 10 testes automatizados cada (20 testes no total) executadas e aprovadas com 100% de taxa de sucesso cobrindo banco, cache, localização, bot e estabilidade.",
    timestamp: new Date(2026, 9, 27, 9, 0),
  },
  // === 13 de Outubro de 2026 (v2.5) ===
  {
    type: "feature",
    title: "Monitoramento de Inatividade & Sessão Contínua no Frontend",
    description: "Novo card em Configurações > Monitoramento de Tempo para controle de logoff automático por inatividade. Possui opção de desativação total (mantendo o operador sempre conectado sem deslogar) e presets rápidos de tempo (30m, 1h, 4h, 8h e até 24h / 1440min) com feedback em tempo real de duração.",
    timestamp: new Date(2026, 9, 13, 14, 30),
  },
  {
    type: "bugfix",
    title: "Correção de Desconexões Espúrias em Produção (Refresh Token Lax)",
    description: "Ajuste na política de segurança de cookies do token de atualização (SameSite: 'lax') e ciclo de vida de sessão no backend. Impede o descarte inadvertido de cookies de autenticação em requisições de subdomínios e links externos em produção.",
    timestamp: new Date(2026, 9, 13, 14, 15),
  },
  {
    type: "improvement",
    title: "Suíte de Testes Automatizados de Integridade de Sessão",
    description: "Bateria de 10 testes automatizados cobrindo schema do PostgreSQL, persistência de configurações, lógica de tolerância temporal e conectividade de ponta a ponta.",
    timestamp: new Date(2026, 9, 13, 14, 0),
  },
  // === 13 de Outubro de 2026 (v2.4) ===
  {
    type: "feature",
    title: "Buffer Anti-Repetição de Menus no WhatsApp (Debounce Inteligente)",
    description: "Agrupamento de mensagens enviadas em sequência rápida pelo cliente (ex: 'Oi', 'Boa noite'). O bot aguarda o intervalo de inatividade configurado antes de enviar o menu, garantindo o disparo de apenas um único menu de atendimento e cancelando o timer imediatamente caso uma opção seja selecionada durante o intervalo.",
    timestamp: new Date(2026, 9, 13, 10, 0),
  },
  {
    type: "feature",
    title: "Trava de Repetição Recente (Cooldown) & Orientação para Opções Fora do Menu",
    description: "Prevenção de poluição visual e looping: se o menu de filas já foi exibido e o cliente digitar mensagens livres ou dúvidas que não correspondam às opções válidas, o bot não reenvia o menu completo repetidamente. Em vez disso, envia uma mensagem orientadora curta e educada para selecionar pelo número, mantendo o suporte ao comando '#' ou 'menu' para reexibição completa.",
    timestamp: new Date(2026, 9, 13, 9, 45),
  },
  {
    type: "improvement",
    title: "Configuração Granular por Conexão de WhatsApp",
    description: "Novos campos integrados na aba 'Mensagens' do modal de Conexões do WhatsApp: personalização da Mensagem de Opção Inválida e ajuste do Tempo de Espera para Saudação (segundos), com persistência em banco de dados e aplicação em tempo real.",
    timestamp: new Date(2026, 9, 13, 9, 30),
  },
  // === 30 de Setembro de 2026 (v2.3) ===
  {
    type: "feature",
    title: "Sincronização e Histórico de Mensagens Encaminhadas no CRM",
    description: "Gravação instantânea e exibição garantida no histórico de conversas do CRM para todas as mensagens encaminhadas (texto, fotos, vídeos, áudios, documentos e contatos vCard). Indicador visual ↪️ 'Encaminhada' integrado nos balões de envio e recebimento com suporte retroativo a mensagens sincronizadas do WhatsApp Web.",
    timestamp: new Date(2026, 8, 30, 8, 30),
  },
  {
    type: "feature",
    title: "Gestão Inteligente de Férias, Folgas e Licença de Atendentes",
    description: "Controle de status de ausência para operadores. Bloqueio proativo e notificação ao transferir atendimentos para atendentes de férias/ausentes, com redirecionamento automático de tickets não atendidos de volta para a fila.",
    timestamp: new Date(2026, 8, 30, 8, 15),
  },
  {
    type: "improvement",
    title: "Limpeza Profunda do Código e Otimização do Ambiente",
    description: "Remoção de dezenas de arquivos de teste legados e componentes órfãos no backend e frontend. Ambiente reconstruído do zero com migrations e seeds consolidados, containers PostgreSQL 13 e Redis 6 otimizados e zero lentidão.",
    timestamp: new Date(2026, 8, 30, 8, 0),
  },
  // === 25 de Setembro de 2026 (v2.2) ===
  {
    type: "feature",
    title: "Encaminhamento de Mensagens e Mídias para Grupos de WhatsApp",
    description: "Recurso completo de encaminhamento e redirecionamento de texto, áudios, fotos, vídeos, figurinhas, vCards e arquivos PDF para grupos no WhatsApp via Baileys com validação em tempo real.",
    timestamp: new Date(2026, 8, 25, 17, 30),
  },
  {
    type: "feature",
    title: "Novo Modal Moderno de Encaminhamento com Abas Segmentadas",
    description: "Interface com abas rápidas (Todos, Contatos, Grupos), contadores visuais, busca instantânea, status de conexão e envio imediato com feedback sonoro e visual.",
    timestamp: new Date(2026, 8, 25, 17, 0),
  },
  {
    type: "improvement",
    title: "Controle Estrito de Permissões e Visibilidade de Grupos",
    description: "Blindagem de segurança: atendentes apenas visualizam grupos aos quais foram autorizados pelo gestor ou grupos públicos. Bloqueio no backend HTTP 403 Forbidden para tentativas de acesso não autorizado.",
    timestamp: new Date(2026, 8, 25, 16, 30),
  },
  {
    type: "feature",
    title: "Painel de Deploy, Instalação e Rollback Automatizado (Porta 9000)",
    description: "Painel de controle autônomo com upload de ZIP, assistente de configuração de portas e banco de dados (.env), execução de migrations, streaming de logs via Socket.IO e restauração de versões em 1 clique.",
    timestamp: new Date(2026, 8, 25, 16, 0),
  },
  {
    type: "feature",
    title: "Monitoramento em Tempo Real & Saúde do Servidor (Hardware & Processos)",
    description: "Diagnósticos ao vivo de CPU, Memória RAM, Armazenamento em Disco, Conexões PostgreSQL e Cache Redis. Ações rápidas de resolução de lentidão (Reiniciar CRM em 3s, Limpar Redis, Vacuum Analyze no banco e Limpeza Inteligente de Mídias).",
    timestamp: new Date(2026, 8, 25, 15, 30),
  },
  {
    type: "improvement",
    title: "Blindagem de URLs Dinâmicas, CORS e Preflight OPTIONS",
    description: "Eliminação total de URLs hardcoded. O sistema agora preserva automaticamente as portas :8080 e :3000 em ambiente local ou IP de VPS, e adota HTTPS direto em domínios de produção com Nginx.",
    timestamp: new Date(2026, 8, 25, 15, 0),
  },
  // === 21 de Setembro de 2026 (v2.1) ===
  {
    type: "feature",
    title: "Modo Observador & Trava de Digitação (Ligar e Desligar Chat)",
    description: "Supervisão segura para administradores e supervisores ao abrir atendimentos de outros colegas (ex: Júlia ou Ricardo). O chat abre automaticamente DESLIGADO no Modo Observador (Apenas Leitura), eliminando qualquer risco de digitação ou envio acidental para o cliente. Inclui botão interativo 'Ligar Chat / Intervir' com faixa de aviso e alternador rápido no cabeçalho.",
    timestamp: new Date(2026, 8, 21, 11, 15),
  },
  {
    type: "improvement",
    title: "Controle de Permissões de Co-atendimento & Ejeção em Tempo Real",
    description: "Blindagem de titularidade: o atendente convidado atua estritamente como suporte e não pode finalizar, transferir ou devolver o ticket para a fila (bloqueado no backend HTTP 403 e botões ocultos). Ao ser removido, o atendente é instantaneamente ejetado da tela do chat e o ticket é removido da barra lateral em tempo real sem F5.",
    timestamp: new Date(2026, 8, 21, 10, 45),
  },
  {
    type: "feature",
    title: "Transcrição Ultrarrápida com Groq Cloud (Whisper Large v3 Turbo)",
    description: "Integração com a API Groq Cloud para transcrição instantânea e gratuita de mensagens de voz e áudios no WhatsApp em menos de 1 segundo. Conta com fallback automático para OpenAI, botão de transcrição sob demanda nas mensagens e transcrição em fluxos de IA.",
    timestamp: new Date(2026, 8, 21, 9, 30),
  },
  {
    type: "bugfix",
    title: "Consistência e Filtro de Contatos Ativos vs Tickets",
    description: "Ajuste na sincronização da lista de contatos, garantindo coerência exata entre os atendimentos ativos e a listagem geral, eliminando divergências na contagem do Dashboard.",
    timestamp: new Date(2026, 8, 21, 8, 0),
  },
  // === 20 de Setembro de 2026 (v2.0) ===
  {
    type: "improvement",
    title: "Arquitetura Zero-Leak & Prevenção de Memory Leaks",
    description: "Eliminação definitiva de avisos de estado em componentes desmontados no Dashboard (useMessages, useContacts, fetchData) e nos modais de aceite de tickets (AcceptTicketWithoutQueueModal). Otimização completa dos ciclos de vida com flags de montagem e cancelamento de timers.",
    timestamp: new Date(2026, 8, 20, 2, 30),
  },
  {
    type: "bugfix",
    title: "Correção de Foco Retido e Acessibilidade em Modais & Navegação",
    description: "Resolução do aviso de blocked aria-hidden ao abrir modais na navegação mobile e menus suspensos. Os botões da barra inferior e itens de menu agora liberam o foco de forma limpa na navegação e clique.",
    timestamp: new Date(2026, 8, 20, 2, 0),
  },
  {
    type: "improvement",
    title: "Padronização de Modais FlowBuilder & Remoção de Props Obsoletas",
    description: "Substituição global de fullWidth strings por maxWidth e fullWidth booleano nos nós do FlowBuilder e Webhooks, além da remoção de propriedades descontinuadas (disableBackdropClick) do Material-UI.",
    timestamp: new Date(2026, 8, 20, 1, 30),
  },
  {
    type: "bugfix",
    title: "Validação Segura de Datas & Rota de Migração",
    description: "Tratamento de strings vazias de datas no Gerenciador de Empresas evitando mensagens de 'Data inválida', e registro correto do roteador de migrações no backend (/migration/config).",
    timestamp: new Date(2026, 8, 20, 1, 0),
  },
  // === 19 de Setembro de 2026 (v1.9) ===
  {
    type: "feature",
    title: "Participantes do Ticket / Co-atendimento entre Atendentes",
    description: "Sistema completo de co-atendimento em tempo real. Permite que um atendente convide outros colegas (com destaque para atendentes da mesma fila) para colaborar no mesmo ticket. Inclui modal flutuante de convite com alerta sonoro, botões para aceitar ou recusar, registro de mensagens internas no chat ('Atendente entrou/saiu') e inclusão automática do ticket na aba 'Atendendo' do co-atendente.",
    timestamp: new Date(2026, 8, 19, 20, 30),
  },
  {
    type: "bugfix",
    title: "Deduplicação Inteligente de Grupos na Sincronização & Mensagens",
    description: "Correção definitiva da duplicação de grupos. Ao receber mensagens em grupos sincronizados, o sistema agora unifica perfeitamente os contatos e reutiliza o mesmo ticket do grupo na aba correta, normalizando os identificadores JID (sem @g.us) e auto-recuperando registros legados.",
    timestamp: new Date(2026, 8, 19, 19, 0),
  },
  {
    type: "improvement",
    title: "Gerenciamento de Permissões de Grupos em Lote",
    description: "Otimização de performance e experiência no modal de permissões de grupos, com filtros rápidos, suporte a paginação e resposta instantânea via consultas otimizadas no backend.",
    timestamp: new Date(2026, 8, 19, 17, 30),
  },
  {
    type: "improvement",
    title: "Limpeza Completa de Warnings & Build Zero-Warning",
    description: "Resolução de todos os avisos de acessibilidade de emojis, chaves duplicadas no React e otimização do Webpack Dev Server, eliminando poluição no console do navegador.",
    timestamp: new Date(2026, 8, 19, 16, 0),
  },
  // === 14 de Julho de 2026 (v1.8) ===
  {
    type: "feature",
    title: "Auditoria Completa de Aniversários",
    description: "Correção crítica do fuso horário na rotina de agendamentos. O sistema agora sincroniza 100% com o Horário Oficial de Brasília. Adicionada camada extra de segurança (Redis) para prevenir disparo duplicado de mensagens em caso de restart.",
    timestamp: new Date(2026, 6, 14, 22, 10),
  },
  {
    type: "bugfix",
    title: "Correção Global do Tema Escuro",
    description: "Refatoração visual nas configurações de Aniversários, WebChat, QR Code, Importação e Menus Superiores. O layout agora se adapta perfeitamente ao modo noturno, sem telas ofuscantes ou botões ilegíveis.",
    timestamp: new Date(2026, 6, 14, 22, 0),
  },
  {
    type: "improvement",
    title: "Otimização Extrema de Performance (Zero-Leak)",
    description: "Refatoração profunda da arquitetura do banco de dados (Subqueries Nativas no Motor do Postgres) nos módulos de Tickets, Kanban, Dashboard e Chatbot. Resolvido o gargalo de consumo de Memória RAM em grandes picos de acesso nas segundas-feiras.",
    timestamp: new Date(2026, 6, 14, 18, 30),
  },
  // === 04 de Julho de 2026 (v1.6) ===
  {
    type: "feature",
    title: "Suporte a Passkey Linking (WhatsApp)",
    description: "Implementação nativa do protocolo Shortcake/CRSC do WhatsApp, permitindo a conexão de contas protegidas por biometria e Passkey sem interrupções, através de uma arquitetura bridge frontend-backend.",
    timestamp: new Date(2026, 6, 4, 18, 0),
  },
  {
    type: "feature",
    title: "Miniaturas (Thumbnails) de PDFs",
    description: "Renderização dinâmica de miniaturas (thumbnails) usando pdf.js diretamente no frontend para pré-visualização de documentos enviados, economizando recursos do servidor e melhorando o layout do chat.",
    timestamp: new Date(2026, 6, 4, 19, 0),
  },
  {
    type: "bugfix",
    title: "Correção de API do Relatório de Origem",
    description: "Correção de um erro 404 onde a rota do relatório de origens (UTM) estava mapeada incorretamente entre o Dashboard e o Backend.",
    timestamp: new Date(2026, 6, 4, 19, 15),
  },
  // === 31 de Maio de 2026 (v1.5) ===
  {
    type: "feature",
    title: "Smart Link - Link Único Inteligente",
    description: "Link único que detecta automaticamente a origem do visitante (Instagram, Facebook, TikTok, WhatsApp, Google, YouTube) e redireciona para o WhatsApp com mensagem personalizada por canal. Suporta variações com UTM para rastreio granular de campanhas e anúncios pagos",
    timestamp: new Date(2026, 4, 31, 20, 0),
  },
  {
    type: "feature",
    title: "WebChat para Sites Externos",
    description: "Widget de atendimento que pode ser instalado em qualquer site (WordPress, HTML, Shopify, etc). Conecta o visitante diretamente ao CRM, criando tickets automaticamente. Configurável via .env.production para gerar o código de integração com as URLs corretas de produção",
    timestamp: new Date(2026, 4, 31, 20, 30),
  },
  {
    type: "feature",
    title: "Relatório de Origem de Contatos (UTM)",
    description: "Nova tela de relatório que rastreia a origem dos contatos via UTM (source, medium, campaign), com resumo por fonte, taxa de conversão, linha do tempo e exportação em CSV. Captura automática de origem em contatos vindos do WebChat",
    timestamp: new Date(2026, 4, 31, 21, 0),
  },
  {
    type: "improvement",
    title: "Detecção Aprimorada do TikTok no Smart Link",
    description: "Melhorada a detecção de origem do TikTok para reconhecer todas as variantes do User-Agent (TikTok, musical_ly, BytedanceWebview), garantindo atribuição correta mesmo em versões antigas do app",
    timestamp: new Date(2026, 4, 31, 21, 30),
  },
  // === 05 de Maio de 2026 (v1.4) ===
  {
    type: "feature",
    title: "Sistema de Delay Configurável para Conexões WhatsApp",
    description: "Implementado delay configurável de 0 a 15 segundos para mensagens de saudação em conexões WhatsApp. Permite simular digitação humana e evitar detecção de bot. Configurável individualmente por conexão",
    timestamp: new Date(2026, 4, 5, 10, 0),
  },
  {
    type: "feature",
    title: "Delay Apenas na Primeira Mensagem",
    description: "Nova opção para aplicar delay apenas na primeira mensagem do bot ou em todas as mensagens. Toggle 'Aplicar delay apenas na primeira mensagem' permite escolher o comportamento ideal para cada conexão",
    timestamp: new Date(2026, 4, 5, 10, 30),
  },
  {
    type: "feature",
    title: "Sistema de Variações de Mensagens (SpinTax)",
    description: "Implementado sistema completo de randomização de mensagens com interface de accordions. Permite adicionar múltiplas variações de mensagens de saudação que são escolhidas aleatoriamente, tornando as conversas mais naturais e menos repetitivas",
    timestamp: new Date(2026, 4, 5, 11, 0),
  },
  {
    type: "feature",
    title: "Saudação Automática por Horário - Variável {{greeting}}",
    description: "Nova variável {{greeting}} que substitui automaticamente por 'Bom dia' (5h-12h), 'Boa tarde' (12h-18h) ou 'Boa noite' (18h-5h) baseado no horário atual. Torna as mensagens mais personalizadas e contextuais",
    timestamp: new Date(2026, 4, 5, 11, 30),
  },
  {
    type: "feature",
    title: "Sistema de Delay e Variações para Usuários/Atendentes",
    description: "Cada usuário/atendente agora pode configurar seu próprio delay (0-15s) e variações de mensagens de saudação e despedida. Mensagens enviadas automaticamente quando o atendente aceita ou finaliza um ticket",
    timestamp: new Date(2026, 4, 5, 14, 0),
  },
  {
    type: "feature",
    title: "Interface Completa com Accordions Editáveis",
    description: "Interface moderna com accordions expansíveis para gerenciar variações de mensagens. Permite adicionar, editar e deletar variações facilmente. Toggle para habilitar/desabilitar o sistema de variações",
    timestamp: new Date(2026, 4, 5, 14, 30),
  },
  {
    type: "improvement",
    title: "Variáveis Dinâmicas em Mensagens de Usuário",
    description: "Suporte completo a variáveis dinâmicas nas mensagens de atendentes: {{greeting}} (saudação por horário), {{name}} (nome completo), {{firstName}} (primeiro nome), {{userName}} (nome do atendente). Substituição automática em tempo real",
    timestamp: new Date(2026, 4, 5, 15, 0),
  },
  {
    type: "improvement",
    title: "Randomização Inteligente de Mensagens",
    description: "Sistema de randomização que garante distribuição uniforme das variações de mensagens. Cada interação escolhe aleatoriamente uma variação diferente, evitando padrões repetitivos",
    timestamp: new Date(2026, 4, 5, 15, 30),
  },
  // === 29 de Abril de 2026 (v1.3) ===
  {
    type: "feature",
    title: "Smart Link - Slug Personalizado por Empresa",
    description: "Cada empresa agora pode definir um slug unico para seu Smart Link (ex: /smart-link/minha-empresa). URLs mais profissionais, com validacao de formato e unicidade. Compatibilidade retroativa com IDs numericos",
    timestamp: new Date(2026, 3, 29, 10, 0),
  },
  {
    type: "feature",
    title: "Relatorio Detalhado de Contatos por Origem",
    description: "Nova aba 'Lista de Contatos' no relatorio de origem com tabela completa: nome, telefone, tags, atendente, fila, origem UTM, campanha, data do primeiro contato, ultimo ticket, status e total de tickets. Com paginacao e exportacao CSV",
    timestamp: new Date(2026, 3, 29, 14, 0),
  },
  {
    type: "improvement",
    title: "Smart Link Dinamico por Empresa",
    description: "Smart Link agora busca configuracoes do banco de dados por empresa. Cada empresa configura seu proprio numero de WhatsApp e mensagens personalizadas por rede social (Instagram, Facebook, WhatsApp, Google/Outros)",
    timestamp: new Date(2026, 3, 29, 11, 0),
  },
  {
    type: "improvement",
    title: "Interface Smart Link Simplificada",
    description: "Removidas redes sociais desnecessarias (TikTok, Twitter, YouTube, LinkedIn). Mantidas apenas 4: Instagram, Facebook, WhatsApp e Google/Outros. Campo de slug personalizado adicionado na configuracao",
    timestamp: new Date(2026, 3, 29, 12, 0),
  },
  {
    type: "improvement",
    title: "Versao do WhatsApp Web Atualizada",
    description: "Versao fallback do WhatsApp Web atualizada para 2.3000.1038079265 com validade ate 24/06/2026. Sistema busca automaticamente a versao mais recente da API do wppconnect na inicializacao",
    timestamp: new Date(2026, 3, 29, 9, 0),
  },
  {
    type: "bugfix",
    title: "Correcao de Rotas Duplicadas no Smart Link",
    description: "Removidas rotas duplicadas no app.ts e corrigida ordem das rotas Express (rotas especificas antes de parametros) para evitar conflitos entre /config, /reload e /:identifier",
    timestamp: new Date(2026, 3, 29, 13, 0),
  },
  {
    type: "bugfix",
    title: "Correcao da Porta do Smart Link",
    description: "Links gerados agora usam corretamente a porta 8080 (backend) ao inves da porta 3000 (frontend), garantindo que os redirecionamentos funcionem",
    timestamp: new Date(2026, 3, 29, 11, 30),
  },
  // === 24 de Abril de 2026 (v1.2) ===
  {
    type: "feature",
    title: "Sistema de Rastreamento UTM Completo",
    description: "Implementado sistema completo de rastreamento de origem de contatos com campos UTM (source, medium, campaign, term, content), referrer, landing page e afiliado. Inclui relatório visual com gráficos, tabelas e exportação CSV",
    timestamp: new Date(2026, 3, 24, 10, 0),
  },
  {
    type: "feature",
    title: "Detecção Automática de Origem por Mensagem",
    description: "Sistema inteligente que detecta automaticamente a origem do contato quando ele menciona palavras-chave como 'instagram', 'tiktok', 'facebook', 'twitter', 'youtube', 'linkedin' ou 'whatsapp' na primeira mensagem",
    timestamp: new Date(2026, 3, 24, 11, 0),
  },
  {
    type: "feature",
    title: "Smart Link - Link Único Inteligente",
    description: "Criado sistema de link único que detecta automaticamente a origem do visitante através de User-Agent e Referrer, permitindo usar um único link na bio de todas as redes sociais. Integrado na porta 8080 sem necessidade de configuração adicional",
    timestamp: new Date(2026, 3, 24, 14, 0),
  },
  {
    type: "feature",
    title: "Interface Visual para Smart Link",
    description: "Página moderna e intuitiva para configurar o Smart Link sem editar arquivos JSON. Permite personalizar número do WhatsApp, mensagens por rede social, gerar links automaticamente e copiar com um clique",
    timestamp: new Date(2026, 3, 24, 16, 0),
  },
  {
    type: "feature",
    title: "Mensagem de Saudação por Usuário",
    description: "Implementado campo de mensagem de saudação personalizada por usuário, enviada automaticamente quando o atendente aceita um ticket. Complementa a funcionalidade de mensagem de despedida já existente",
    timestamp: new Date(2026, 3, 24, 9, 0),
  },
  {
    type: "improvement",
    title: "Relatório de Origem de Contatos",
    description: "Dashboard completo com 4 cards de métricas, 3 gráficos interativos (pizza, barras e linha temporal), tabela detalhada com filtros por data e exportação para CSV. Mostra origem, quantidade de contatos e taxa de conversão",
    timestamp: new Date(2026, 3, 24, 12, 0),
  },
  {
    type: "improvement",
    title: "Captura de UTM em Landing Pages",
    description: "Sistema de captura automática de parâmetros UTM em landing pages públicas, salvando no localStorage e enviando junto com o formulário de contato",
    timestamp: new Date(2026, 3, 24, 13, 0),
  },
  // === 31 de Março de 2026 (v1.2) ===
  {
    type: "bugfix",
    title: "Notificação não deletava mais o ticket",
    description: "Corrigido bug onde clicar no X da notificação fechava/deletava o ticket. Agora o X apenas dispensa a notificação sem afetar o ticket",
    timestamp: new Date(2026, 2, 31, 20, 0),
  },
  {
    type: "improvement",
    title: "Notificações em tempo real",
    description: "Corrigido delay de 2-3 segundos nas notificações. O sistema agora usa merge inteligente entre HTTP e WebSocket, evitando sobrescrita de dados em tempo real",
    timestamp: new Date(2026, 2, 31, 20, 15),
  },
  {
    type: "bugfix",
    title: "Notificações desktop no canto da tela",
    description: "Corrigido problema onde as notificações do navegador não apareciam. Agora solicita permissão no primeiro clique e funciona para tickets pendentes com a configuração habilitada",
    timestamp: new Date(2026, 2, 31, 20, 30),
  },
  {
    type: "bugfix",
    title: "Erro ao criar empresa",
    description: "Corrigido erro de sequence do PostgreSQL que impedia criação de novas empresas (SequelizeUniqueConstraintError) e tratamento de erro que causava crash HTTP (ERR_HTTP_INVALID_STATUS_CODE)",
    timestamp: new Date(2026, 2, 31, 20, 45),
  },
  {
    type: "bugfix",
    title: "Erro ao deletar empresa",
    description: "Corrigido erro de foreign key ao deletar empresa. O sistema agora limpa todas as 66 tabelas dependentes antes de remover a empresa, usando SAVEPOINT para resiliência",
    timestamp: new Date(2026, 2, 31, 21, 0),
  },
  {
    type: "improvement",
    title: "Popup de notificação redesenhado",
    description: "Popup de notificações simplificado com avatar, nome do contato, última mensagem e botão de dispensar. Não interfere mais com ações do ticket",
    timestamp: new Date(2026, 2, 31, 21, 15),
  },
  {
    type: "improvement",
    title: "Correção de vulnerabilidades do frontend",
    description: "Atualizados axios (DoS via __proto__), lodash, socket.io-parser, dompurify e outras 21 dependências com vulnerabilidades conhecidas",
    timestamp: new Date(2026, 2, 31, 21, 30),
  },
  {
    type: "improvement",
    title: "Limpeza de logs de debug",
    description: "Removidos console.log de debug do sistema de notificações que causavam overhead desnecessário em produção",
    timestamp: new Date(2026, 2, 31, 21, 45),
  },
  // === 09 de Março de 2026 (v1.1) ===
  {
    type: "feature",
    title: "Alerta Global de Desconexão",
    description: "Novo alerta em tela cheia quando uma conexão WhatsApp cai, com barra de progresso de reconexão automática e contagem de tentativas (até 5x)",
    timestamp: new Date(2026, 2, 9, 10, 0),
  },
  {
    type: "feature",
    title: "Modal de Conexões Fantasma",
    description: "Novo modal para identificar e gerenciar conexões fantasma (sem número vinculado) com opção de remoção em lote",
    timestamp: new Date(2026, 2, 9, 10, 30),
  },
  {
    type: "feature",
    title: "Redesign do Modal QR Code",
    description: "Modal de QR Code redesenhado com visual moderno, indicador de tentativas e instruções claras para o usuário",
    timestamp: new Date(2026, 2, 9, 11, 0),
  },
  {
    type: "feature",
    title: "Health Check de Conexões",
    description: "Monitoramento automático a cada minuto com ping real ao WhatsApp para detectar conexões inativas e alertar o usuário",
    timestamp: new Date(2026, 2, 9, 11, 30),
  },
  {
    type: "improvement",
    title: "Estabilidade de Conexão WhatsApp",
    description: "Versão do WhatsApp Web atualizada, configurações de keepalive otimizadas (25s), browser corrigido para Browsers.macOS('Chrome') e remoção de monitor que causava falsos positivos de desconexão",
    timestamp: new Date(2026, 2, 9, 14, 0),
  },
  {
    type: "improvement",
    title: "Reconexão Automática Inteligente",
    description: "Sistema de reconexão com até 5 tentativas automáticas com backoff exponencial. Após 5 falhas, gera novo QR Code automaticamente",
    timestamp: new Date(2026, 2, 9, 14, 30),
  },
  {
    type: "improvement",
    title: "Botão Reiniciar Todas as Conexões",
    description: "Novo botão na página de conexões para reiniciar todas as conexões WhatsApp de uma vez",
    timestamp: new Date(2026, 2, 9, 15, 0),
  },
  {
    type: "bugfix",
    title: "Correção de Desconexão Manual",
    description: "Corrigido problema onde desconectar manualmente pelo botão causava alertas falsos de queda de conexão",
    timestamp: new Date(2026, 2, 9, 16, 0),
  },
  {
    type: "bugfix",
    title: "Correção de Logs Excessivos no Backend",
    description: "Removidos logs de debug que sobrecarregavam o terminal do servidor, melhorando a performance geral do sistema",
    timestamp: new Date(2026, 2, 9, 16, 30),
  },
  {
    type: "bugfix",
    title: "Correção de Crash ao Desconectar/Logar",
    description: "Resolvido erro que causava crash do backend ao desconectar uma sessão WhatsApp ou ao fazer login no sistema",
    timestamp: new Date(2026, 2, 9, 17, 0),
  },
  // === 08 de Março de 2026 ===
  {
    type: "improvement",
    title: "Página de Conexões Redesenhada",
    description: "Layout da página de conexões atualizado com cards modernos, indicadores visuais de status e ações rápidas por conexão",
    timestamp: new Date(2026, 2, 8, 10, 0),
  },
  {
    type: "bugfix",
    title: "Correção de Build do Frontend (Windows)",
    description: "Corrigido comando de build do frontend para compatibilidade com Windows (substituído NODE_OPTIONS por cross-env)",
    timestamp: new Date(2026, 2, 8, 11, 0),
  },
  {
    type: "bugfix",
    title: "Correção de Build do Backend",
    description: "Resolvido erro de compilação TypeScript no wbotMessageListener.ts que impedia o build do backend",
    timestamp: new Date(2026, 2, 8, 11, 30),
  },
  // === 02 de Março de 2026 (v1.0) ===
  {
    type: "feature",
    title: "Modal de Versão e Atualizações",
    description: "Novo modal no dashboard para visualizar o histórico de atualizações do sistema com changelog organizado por data",
    timestamp: new Date(2026, 2, 2, 10, 30),
  },
];

const VersionModal = ({ open, onClose, version = 'v2.6' }) => {
  const classes = useStyles();
  const updates = CHANGELOG;

  const getUpdateIcon = (type) => {
    switch (type) {
      case 'feature':
        return <CheckCircleIcon style={{ color: '#4caf50' }} />;
      case 'bugfix':
        return <BugReportIcon style={{ color: '#ff9800' }} />;
      case 'improvement':
        return <BuildIcon style={{ color: '#2196f3' }} />;
      default:
        return <InfoIcon style={{ color: '#9e9e9e' }} />;
    }
  };

  const getUpdateTypeLabel = (type) => {
    const labels = {
      feature: 'Nova Funcionalidade',
      bugfix: 'Correção de Bug',
      improvement: 'Melhoria',
      other: 'Atualização',
    };
    return labels[type] || labels.other;
  };

  const groupUpdatesByDate = (updates) => {
    const grouped = {};
    
    updates.forEach((update) => {
      const date = new Date(update.timestamp);
      const dateKey = date.toLocaleDateString('pt-BR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(update);
    });

    return grouped;
  };

  const groupedUpdates = groupUpdatesByDate(updates);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Atualizações do Sistema</Typography>
          <Chip
            label={version}
            size="small"
            className={classes.versionBadge}
          />
        </Box>
        <Button
          color="inherit"
          size="small"
          onClick={onClose}
          startIcon={<CloseIcon />}
        >
          Fechar
        </Button>
      </DialogTitle>

      <DialogContent dividers className={classes.contentContainer}>
        {updates.length === 0 ? (
          <Box className={classes.emptyState}>
            <InfoIcon style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
            <Typography variant="body2">
              Nenhuma atualização registrada
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" className={classes.dateInfo}>
              Histórico completo de atualizações
            </Typography>
            <Divider style={{ margin: '16px 0' }} />

            {Object.entries(groupedUpdates).map(([dateKey, dateUpdates]) => (
              <Box key={dateKey} className={classes.dateGroup}>
                <Typography className={classes.dateHeader}>
                  {dateKey}
                </Typography>
                
                {dateUpdates.map((update, index) => (
                  <Box key={index} className={classes.updateItem}>
                    <Box className={classes.updateType}>
                      {getUpdateIcon(update.type)}
                      <Chip
                        label={getUpdateTypeLabel(update.type)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {update.title}
                    </Typography>
                    <Typography className={classes.updateDescription}>
                      {update.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionModal;
